import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Filter, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { cn } from '@/shared/lib/cn';
import {
  DEFAULT_MANTENIMIENTO_STATUSES,
  MANTENIMIENTO_STATUS_OPTIONS,
  listMantenimiento,
  type MantenimientoRow,
  type MantenimientoStatus,
} from './api';
import { MantenimientoTable } from './components/MantenimientoTable';
import { VentaDetallesDialog } from './components/VentaDetallesDialog';
import { BitacorasQuickDialog } from './components/BitacorasQuickDialog';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';

/**
 * Mantenimiento main page — post-payment fulfillment hub. Matches the
 * PowerApps Screen_Mantenimiento layout: toolbar with reload + search +
 * Filtrar dropdown, a full gallery with the 8 columns (Status ·
 * N° Cotización · F. Venta · Vendedor · Cliente · Teléfono · Correo ·
 * Acciones), and a `Cantidad de ventas` counter footer.
 *
 * Per-row actions use the same 3-icon split as PowerApps + our extra
 * full-detail icon:
 *   - Bitácoras quick popup (note icon)
 *   - Venta detalles popup (cart icon)
 *   - Full M1-M4 detail dialog (eye icon)
 */
export function MantenimientoPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { config: activeCountry } = useActiveCountry();
  const [search, setSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<MantenimientoStatus[]>(
    DEFAULT_MANTENIMIENTO_STATUSES,
  );
  const [viewingDetalles, setViewingDetalles] = useState<MantenimientoRow | null>(null);
  const [viewingBitacoras, setViewingBitacoras] = useState<MantenimientoRow | null>(null);

  // Stable key for React Query — sort so set semantics don't cause refetches
  // when the user toggles statuses back into the same set in a different order.
  const statusKey = useMemo(
    () => [...selectedStatuses].sort().join(','),
    [selectedStatuses],
  );

  const mantenimientoQ = useQuery({
    // Country in the key — mantenimiento rows live on sales_headers
    // which are country-scoped server-side.
    queryKey: ['mantenimiento', 'list', activeCountry.code, statusKey] as const,
    queryFn: () =>
      selectedStatuses.length > 0
        ? listMantenimiento({ statuses: selectedStatuses })
        : Promise.resolve({ rows: [], totalRowCount: 0 }),
    staleTime: 30_000,
  });

  const allRows = mantenimientoQ.data?.rows ?? [];
  const q = search.trim().toLowerCase();
  const filtered = q
    ? allRows.filter((r) => {
        const cliente = (r.fields.Cliente_RO ?? '').toLowerCase();
        const vendedor = (r.fields.Vendedor_RO ?? '').toLowerCase();
        const telefono = r.telefono.toLowerCase();
        const correo = r.correo.toLowerCase();
        const id = r.id.toLowerCase();
        return (
          cliente.includes(q) ||
          vendedor.includes(q) ||
          id.includes(q) ||
          telefono.includes(q) ||
          correo.includes(q)
        );
      })
    : allRows;

  const toggleStatus = (s: MantenimientoStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  };

  const filterLabel =
    selectedStatuses.length === 0
      ? 'Sin estado'
      : selectedStatuses.length === MANTENIMIENTO_STATUS_OPTIONS.length
        ? 'Todos los estados'
        : selectedStatuses.length === 1
          ? selectedStatuses[0]
          : `${selectedStatuses.length} estados`;

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Toolbar — DOM order matches desktop (Title, Refresh, Search,
          Filtrar). On mobile a `basis-full` break drops Search +
          Filtrar onto a second row, leaving the title row with just
          Title + Refresh. */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Mantenimiento
        </h1>
        <Button
          size="icon"
          className="h-12 w-12 shrink-0 bg-foreground text-background hover:bg-foreground/90"
          onClick={() => qc.invalidateQueries({ queryKey: ['mantenimiento'] })}
          aria-label="Actualizar"
          title="Actualizar"
        >
          <RefreshCw
            className={cn('h-4 w-4', mantenimientoQ.isFetching && 'animate-spin')}
            aria-hidden
          />
        </Button>
        {/* Forces search + filtrar onto a new row on mobile only. */}
        <div className="basis-full sm:hidden" aria-hidden />
        <div className="relative min-w-[10rem] flex-1 sm:w-64 sm:flex-none">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            variant="filled"
            placeholder="Búsqueda"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar"
          />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-10 gap-2">
              <Filter className="h-4 w-4" aria-hidden />
              {filterLabel}
              <ChevronDown className="h-4 w-4" aria-hidden />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-2">
            <div className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Estado
            </div>
            {MANTENIMIENTO_STATUS_OPTIONS.map((s) => (
              <StatusCheckboxRow
                key={s}
                label={s}
                checked={selectedStatuses.includes(s)}
                onToggle={() => toggleStatus(s)}
              />
            ))}
            <div className="mt-1 flex items-center justify-between border-t pt-2 text-xs">
              <button
                type="button"
                className="px-1 text-muted-foreground hover:text-foreground"
                onClick={() =>
                  setSelectedStatuses([...MANTENIMIENTO_STATUS_OPTIONS])
                }
              >
                Seleccionar todos
              </button>
              <button
                type="button"
                className="px-1 text-muted-foreground hover:text-foreground"
                onClick={() => setSelectedStatuses(DEFAULT_MANTENIMIENTO_STATUSES)}
              >
                Restablecer
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <MantenimientoTable
        rows={filtered}
        loading={mantenimientoQ.isPending}
        error={mantenimientoQ.isError}
        searchActive={Boolean(q)}
        onView={(row) => navigate(`/mantenimiento/${encodeURIComponent(row.id)}`)}
        onViewDetalles={(row) => setViewingDetalles(row)}
        onViewBitacoras={(row) => setViewingBitacoras(row)}
        onRetry={() => mantenimientoQ.refetch()}
      />

      <div className="flex shrink-0 items-center justify-end">
        <div className="inline-flex items-center gap-3 rounded-lg border bg-card px-4 py-2 text-sm">
          <span className="text-muted-foreground">Cantidad de ventas</span>
          <span className="font-semibold tabular-nums text-foreground">
            {filtered.length}
          </span>
        </div>
      </div>

      {viewingDetalles && (
        <VentaDetallesDialog
          row={viewingDetalles}
          onClose={() => setViewingDetalles(null)}
        />
      )}
      {viewingBitacoras && (
        <BitacorasQuickDialog
          row={viewingBitacoras}
          onClose={() => setViewingBitacoras(null)}
        />
      )}
    </div>
  );
}

function StatusCheckboxRow({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={cn(
        'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-muted',
        checked && 'bg-muted/60',
      )}
    >
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-border accent-primary"
        checked={checked}
        onChange={onToggle}
      />
      <span className="flex-1 truncate">{label}</span>
    </label>
  );
}
